import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { NO_AUTH_CONTEXT } from '@app/tokens';
import config from '@config';
import { AuthService } from '@services/auth.service';
import { LogService } from '@services/log.service';
import { UrlService } from '@services/url.service';
import { UserService } from '@services/user.service';
import { FeatureFlagContextAttributes } from '@shared/models/feature-flag-context-attributes';
import { catchError, firstValueFrom, of } from 'rxjs';

type FeatureFlagValue = boolean | string | number;

@Injectable({ providedIn: 'root' })
export class FeatureFlagsService {
  readonly authService = inject(AuthService);
  readonly httpClient = inject(HttpClient);
  readonly logService = inject(LogService);
  readonly urlService = inject(UrlService);
  readonly userService = inject(UserService);

  private readonly flags = signal<Record<string, FeatureFlagValue>>({});
  private readonly overrides = new Map<string, FeatureFlagValue>(Object.entries(config.featureFlags ?? {}));
  private readonly contextFlagsCache = new Map<string, Promise<Record<string, FeatureFlagValue>>>();

  private loadGeneration = 0;

  public constructor() {
    this.userService.userChanged.subscribe(() => this.reload());
  }

  async load(): Promise<void> {
    const generation = ++this.loadGeneration;
    const isAuthenticated = this.authService.authStatus();
    const url = isAuthenticated ? this.urlService.FEATURE_FLAGS_URL : this.urlService.FEATURE_FLAGS_PUBLIC_URL;
    const response = await firstValueFrom(
      this.httpClient.get<{ flags: Record<string, FeatureFlagValue> }>(url, isAuthenticated ? {} : { context: NO_AUTH_CONTEXT }).pipe(
        catchError(err => {
          this.logService.error(new Error(`Failed to load feature flags from ${url}`, { cause: err }));
          return of({ flags: {} });
        }),
      ),
    );
    if (generation !== this.loadGeneration) return;
    this.flags.set(response.flags);
  }

  async reload(): Promise<void> {
    this.contextFlagsCache.clear();
    await this.load();
  }

  async isOnWithContext(key: string, context: FeatureFlagContextAttributes): Promise<boolean> {
    const override = this.overrides.get(key);
    if (override !== undefined) return this.toBoolean(override);

    if (this.isContextEmpty(context)) return this.toBoolean(this.flags()[key]);

    const flags = await this.loadContextFlags(context);
    return this.toBoolean(flags[key]);
  }

  is(key: string, defaultResult = true): boolean {
    return this.exists(key) ? this.isOn(key) : defaultResult;
  }

  isOn(key: string): boolean {
    const override = this.overrides.get(key);

    return override !== undefined ? this.toBoolean(override) : this.toBoolean(this.flags()[key]);
  }

  select(key: string) {
    return computed(() => this.isOn(key));
  }

  getStringValue(key: string, fallback?: string): string | undefined {
    const override = this.overrides.get(key);
    if (override !== undefined) return String(override);

    const value = this.flags()[key];
    return value !== undefined ? String(value) : fallback;
  }

  exists(key: string): boolean {
    return this.overrides.has(key) || key in this.flags();
  }

  private loadContextFlags(context: FeatureFlagContextAttributes): Promise<Record<string, FeatureFlagValue>> {
    const cacheKey = JSON.stringify(context);
    let request = this.contextFlagsCache.get(cacheKey);
    if (!request) {
      request = firstValueFrom(
        this.httpClient.post<{ flags: Record<string, FeatureFlagValue> }>(this.urlService.FEATURE_FLAGS_CONTEXT_URL, context),
      )
        .then(response => response.flags)
        .catch(err => {
          this.logService.error(new Error('Failed to load contextual feature flags', { cause: err }));
          this.contextFlagsCache.delete(cacheKey);
          return {};
        });
      this.contextFlagsCache.set(cacheKey, request);
    }

    return request;
  }

  private isContextEmpty(context: FeatureFlagContextAttributes): boolean {
    return Object.values(context).every(value => !Array.isArray(value) || value.length === 0);
  }

  private toBoolean(value: FeatureFlagValue | undefined): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized !== '' && normalized !== 'false' && normalized !== '0';
    }

    return false;
  }
}
